/**
 * User-preference cache (BUG-S1043-001 / BUG-S1043-002 fix — FINDING-S980-A family).
 *
 * Background: /settings/general persists `dateFormat` and `timezone` (in `displaySettings`).
 * Before this fix, the values were orphan data — no formatter consumed them. With the
 * single-source-of-truth `formatDate` / `formatDateTime`, we keep a module-level cache
 * populated by `setUserDateFormat` / `setUserTimezone` on app boot (AuthContext) and on
 * settings save (GeneralSettingsPage). Lookups stay synchronous so existing call sites
 * (~40 of them) need no changes.
 *
 * Defaults match the pre-fix behavior so users who never visit /settings/general see
 * no change — this is a strict additive enhancement.
 */
let _userDateFormat: string | null = null; // null = unconfigured (use Thai short locale as before)
let _userTimezone: string = 'Asia/Bangkok';

// R180 fix: the backend serializes naive ICT datetimes (thailand_now_naive) with NO
// timezone offset, e.g. "2026-07-03T12:02:28". `new Date()` parses an offset-less
// datetime as BROWSER-LOCAL, so a formatter that then converts to Asia/Bangkok via
// toLocaleString({timeZone}) double-shifts the value on any non-Bangkok browser
// (12:02 → 19:02 in UTC, 23:02 in New York). Append the ICT offset so the instant is
// correct regardless of browser timezone. Only touch offset-less date-TIME strings —
// date-only strings and already-offset strings are left as-is.
const ICT_OFFSET = '+07:00';
const _isOffsetlessDateTime = (s: string): boolean =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !/[Zz]$|[+-]\d{2}:?\d{2}$/.test(s);
export const toIctInstant = (dateString: string): string =>
  _isOffsetlessDateTime(dateString) ? dateString + ICT_OFFSET : dateString;

const SUPPORTED_DATE_FORMATS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'] as const;

export const setUserDateFormat = (fmt: string | undefined | null): void => {
  if (typeof fmt === 'string' && (SUPPORTED_DATE_FORMATS as readonly string[]).includes(fmt)) {
    _userDateFormat = fmt;
  } else {
    _userDateFormat = null;
  }
};

export const setUserTimezone = (tz: string | undefined | null): void => {
  if (typeof tz === 'string' && tz.trim()) {
    _userTimezone = tz;
  }
};

export const getUserDateFormat = (): string | null => _userDateFormat;
export const getUserTimezone = (): string => _userTimezone;

const formatDateWithUserPref = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  switch (_userDateFormat) {
    case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
    case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
    case 'DD-MM-YYYY': return `${day}-${month}-${year}`;
    case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
    default: return `${day}/${month}/${year}`;
  }
};

/**
 * ฟังก์ชันสำหรับจัดรูปแบบวันที่แบบภาษาไทย
 * @param dateString - สตริงวันที่ที่ต้องการจัดรูปแบบ
 * @returns สตริงวันที่ในรูปแบบ วัน เดือน ปี พ.ศ.
 */
export const formatThaiDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting Thai date:', error);
    return '-';
  }
};

/**
 * ฟังก์ชันสำหรับจัดรูปแบบวันที่
 * @param dateString - สตริงวันที่ที่ต้องการจัดรูปแบบ
 * @param locale - ภาษาที่ต้องการแสดงผล (ค่าเริ่มต้น: th-TH)
 * @returns สตริงวันที่ที่จัดรูปแบบแล้ว
 */
export const formatDate = (dateString: string | undefined, locale = 'th-TH'): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);

    // ตรวจสอบว่าวันที่ถูกต้องหรือไม่
    if (isNaN(date.getTime())) {
      return '-';
    }

    // BUG-S1043-001 fix: honor user dateFormat preference if set (FINDING-S980-A).
    // Unconfigured → fall through to Thai short locale (backward-compatible).
    if (_userDateFormat) {
      return formatDateWithUserPref(date);
    }

    return date.toLocaleDateString(locale, {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * ฟังก์ชันสำหรับจัดรูปแบบวันเวลา
 * @param dateString - สตริงวันที่ที่ต้องการจัดรูปแบบ
 * @param locale - ภาษาที่ต้องการแสดงผล (ค่าเริ่มต้น: th-TH)
 * @returns สตริงวันที่และเวลาที่จัดรูปแบบแล้ว
 */
export const formatDateTime = (dateString: string | undefined, locale = 'th-TH'): string => {
  if (!dateString) return '-';

  try {
    // R180: normalize naive-ICT backend datetimes so the instant is correct before
    // toLocaleString({timeZone}) converts it (was double-shifting on non-Bangkok browsers).
    const date = new Date(toIctInstant(dateString));
    
    // ตรวจสอบว่าวันที่ถูกต้องหรือไม่
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    // BUG-S1043-002 fix: honor user timezone preference if set; default Asia/Bangkok.
    return date.toLocaleString(locale, {
      year: '2-digit',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: _userTimezone || 'Asia/Bangkok'
    });
  } catch (error) {
    console.error('Error formatting date time:', error);
    return '-';
  }
};

/**
 * ฟังก์ชันสำหรับคำนวณจำนวนวันระหว่างวันที่สองวัน
 * @param startDate - วันที่เริ่มต้น
 * @param endDate - วันที่สิ้นสุด
 * @returns จำนวนวันระหว่างวันที่สองวัน
 */
export const calculateDaysBetween = (startDate: Date, endDate: Date): number => {
  try {
    // คำนวณจำนวนมิลลิวินาทีระหว่างวันที่
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    
    // แปลงเป็นจำนวนวัน
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculating days between dates:', error);
    return 0;
  }
};

/**
 * ฟังก์ชันสำหรับคำนวณวันที่ครบกำหนด โดยเพิ่มจำนวนวันเข้าไปในวันที่เริ่มต้น
 * @param startDate - วันที่เริ่มต้น
 * @param days - จำนวนวันที่ต้องการเพิ่ม
 * @returns วันที่ครบกำหนด
 */
export const calculateDueDate = (startDate: Date, days: number): Date => {
  try {
    const dueDate = new Date(startDate);
    dueDate.setDate(dueDate.getDate() + days);
    return dueDate;
  } catch (error) {
    console.error('Error calculating due date:', error);
    return new Date();
  }
};

/**
 * ฟังก์ชันสำหรับคำนวณจำนวนวันที่เหลือจนถึงวันที่ที่กำหนด
 * @param targetDate - วันที่เป้าหมาย
 * @returns จำนวนวันที่เหลือ (ติดลบหากผ่านไปแล้ว)
 */
export const calculateRemainingDays = (targetDate: Date): number => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const target = new Date(targetDate);
    target.setHours(0, 0, 0, 0);
    
    const diffTime = target.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  } catch (error) {
    console.error('Error calculating remaining days:', error);
    return 0;
  }
};

/**
 * ฟังก์ชันสำหรับตรวจสอบว่าวันที่ใกล้หมดอายุภายในจำนวนวันที่กำหนดหรือไม่
 * @param targetDate - วันที่เป้าหมาย
 * @param days - จำนวนวันสำหรับการตรวจสอบ (ค่าเริ่มต้น: 30 วัน)
 * @returns true ถ้าใกล้หมดอายุ, false ถ้าไม่ใช่
 */
export const isExpiringSoon = (targetDate: Date | string, days = 30): boolean => {
  try {
    const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
    const remainingDays = calculateRemainingDays(target);
    
    return remainingDays >= 0 && remainingDays <= days;
  } catch (error) {
    console.error('Error checking if date is expiring soon:', error);
    return false;
  }
};

/**
 * ฟังก์ชันสำหรับตรวจสอบว่าวันที่หมดอายุแล้วหรือไม่
 * @param targetDate - วันที่เป้าหมาย
 * @returns true ถ้าหมดอายุแล้ว, false ถ้ายังไม่หมดอายุ
 */
export const isExpired = (targetDate: Date | string): boolean => {
  try {
    const target = typeof targetDate === 'string' ? new Date(targetDate) : targetDate;
    const remainingDays = calculateRemainingDays(target);
    
    return remainingDays < 0;
  } catch (error) {
    console.error('Error checking if date is expired:', error);
    return false;
  }
};

/**
 * ฟังก์ชันสำหรับแปลงวันที่ในรูปแบบสตริงเป็น Date object
 * @param dateString - สตริงวันที่ที่ต้องการแปลง (รองรับรูปแบบ YYYY-MM-DD)
 * @returns Date object หรือ null ถ้าแปลงไม่สำเร็จ
 */
export const parseDate = (dateString: string): Date | null => {
  try {
    if (!dateString) return null;
    
    // กรณีที่เป็นรูปแบบ YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // กรณีอื่นๆ
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * ฟังก์ชันสำหรับแปลงวันที่เป็นสตริงในรูปแบบ YYYY-MM-DD
 * @param date - Date object ที่ต้องการแปลง
 * @returns สตริงวันที่ในรูปแบบ YYYY-MM-DD หรือสตริงว่างถ้าแปลงไม่สำเร็จ
 */
export const formatDateToString = (date: Date | null | undefined): string => {
  try {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error formatting date to string:', error);
    return '';
  }
};

/**
 * ตรวจสอบว่าข้อมูลเคยถูกแก้ไขจริงหรือไม่
 * จะคืนค่า true ถ้าข้อมูลไม่เคยถูกแก้ไข (ไม่ควรแสดงข้อมูลการแก้ไข)
 * จะคืนค่า false ถ้าข้อมูลเคยถูกแก้ไขแล้ว (ควรแสดงข้อมูลการแก้ไข)
 * @param updatedAt - วันที่แก้ไขข้อมูลล่าสุด
 * @param updatedBy - ผู้แก้ไขข้อมูลล่าสุด
 * @returns true ถ้าไม่เคยแก้ไข, false ถ้าเคยแก้ไขแล้ว
 */
export const isNeverUpdated = (updatedAt: string | null | undefined, updatedBy?: any): boolean => {
  // ถ้า updated_at หรือ updated_by เป็น null หมายความว่าไม่เคยแก้ไข
  return !updatedAt || !updatedBy;
};

/**
 * ฟังก์ชันสำหรับได้รับสตริงวันที่ในรูปแบบ YYYY-MM-DD (Local time)
 * ใช้สำหรับ input type="date" value - แก้ปัญหา timezone ที่เกิดจากการใช้ toISOString()
 * @param date - Date object หรือสตริงวันเวลา (ถ้าไม่ระบุจะใช้วันที่ปัจจุบัน)
 * @returns สตริงวันที่ในรูปแบบ YYYY-MM-DD (Local time)
 */
export const getLocalDateString = (date?: Date | string | null): string => {
  try {
    const d = date ? new Date(date) : new Date();

    // ถ้า date ไม่ valid ให้ใช้วันที่ปัจจุบัน
    const targetDate = isNaN(d.getTime()) ? new Date() : d;

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch (error) {
    console.error('Error getting local date string:', error);
    // Fallback: ใช้วันที่ปัจจุบัน
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
};

/**
 * ฟังก์ชันสำหรับแปลงวันเวลาเป็นสตริงรูปแบบ datetime-local input (YYYY-MM-DDTHH:MM)
 * ใช้เวลาท้องถิ่น (Local time) ไม่ใช่ UTC
 * @param date - Date object หรือสตริงวันเวลา (ถ้าไม่ระบุจะใช้เวลาปัจจุบัน)
 * @returns สตริงวันเวลาในรูปแบบ YYYY-MM-DDTHH:MM (Local time)
 */
export const toLocalDateTimeInputValue = (date?: Date | string | null): string => {
  try {
    const d = date ? new Date(date) : new Date();

    // ถ้า date ไม่ valid ให้ใช้เวลาปัจจุบัน
    const targetDate = isNaN(d.getTime()) ? new Date() : d;

    const year = targetDate.getFullYear();
    const month = String(targetDate.getMonth() + 1).padStart(2, '0');
    const day = String(targetDate.getDate()).padStart(2, '0');
    const hours = String(targetDate.getHours()).padStart(2, '0');
    const minutes = String(targetDate.getMinutes()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (error) {
    console.error('Error converting to local datetime input value:', error);
    // Fallback: ใช้เวลาปัจจุบัน
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }
};

/**
 * จัดรูปแบบวันที่แบบเต็มสำหรับแสดงในโหมดดูรายละเอียด
 * @param dateString - สตริงวันที่ที่ต้องการจัดรูปแบบ
 * @param locale - ภาษาที่ต้องการแสดงผล (ค่าเริ่มต้น: th-TH)
 * @returns สตริงวันที่และเวลาที่จัดรูปแบบแล้วแบบเต็ม
 */
export const formatDateTimeFull = (dateString: string | null | undefined, locale = 'th-TH'): string => {
  if (!dateString) return '-';

  try {
    // R181: normalize naive-ICT backend datetime (same fix as formatDateTime, R180)
    const date = new Date(toIctInstant(dateString));

    if (isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Bangkok'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};